import { api } from './client';

// 分渠道流量 (网站分析 - 流量监控) 的一行数据。
// 对应观远 BI 卡片列：一级/二级/三级渠道、UV、支付用户数、UV转化率、净销售额、单UV价值。
export interface ChannelTrafficRow {
  one_level_channel: string;
  two_level_channel?: string;
  three_level_channel?: string;
  uv: number;
  pay_users: number;
  uv_conversion_rate: number;
  net_sales: number;
  uv_value: number;
}

export interface ChannelTrafficData {
  list: ChannelTrafficRow[];
  site: string;
  date: string;
  level: number;
}

export interface ChannelTrafficParams {
  site: string;
  date: string;
  level?: 1 | 2 | 3;
}

// 后端统一响应包裹：{ code, message, data }
interface ApiEnvelope<T> {
  code: number;
  message: string;
  data: T;
}

// ── 网站跳出率 (网站分析 - 网站跳出率) ──────────────────────────────
export interface BounceSummary {
  all_site_uv: number;
  jump_uv: number;
  no_jump_uv: number;
  bounce_rate: number;
}

export interface BounceTrendRow {
  date: string;
  user_type: string; // 全部 / 已支付 / 未支付 / 未注册
  all_site_uv: number;
  jump_uv: number;
  bounce_rate: number;
}

export interface BounceChannelRow {
  one_level_channel: string;
  two_level_channel?: string;
  overall_uv: number;
  overall_jump: number;
  overall_rate: number;
  paid_uv: number;
  paid_jump: number;
  paid_rate: number;
  unpaid_uv: number;
  unpaid_jump: number;
  unpaid_rate: number;
  unreg_uv: number;
  unreg_jump: number;
  unreg_rate: number;
}

export interface BounceDetailRow {
  date: string;
  user_type: string;
  pub_client?: string;
  channel?: string;
  all_site_uv: number;
  jump_uv: number;
  no_jump_uv: number;
  bounce_rate: number;
}

export interface BounceParams {
  site: string;
  start_date: string;
  end_date: string;
  user_type?: string;
  pub_client?: string;
  level?: 1 | 2;
  group_by?: 'user_type' | 'user_type_device' | 'user_type_channel';
}

export const siteDataApi = {
	getReportLabels: (params: { dataset: string; field: string; site: string; search?: string; page?: number; size?: number; all?: boolean; limit?: number }) =>
	  api.get<ApiEnvelope<{ list: string[]; total: number }>>('/site-data/report-labels', { params }),
	getReportLabelPage: (params: { dataset: string; fields: string[]; site: string }) =>
	  api.get<ApiEnvelope<{ fields: Record<string, { list: string[]; total: number }> }>>('/site-data/report-labels', { params: { dataset: params.dataset, site: params.site, fields: params.fields.join(',') } }),
  getSites: () => api.get<ApiEnvelope<{ list: string[] }>>('/site-data/sites'),
  getChannelTraffic: (params: ChannelTrafficParams) =>
    api.get<ApiEnvelope<ChannelTrafficData>>('/site-data/traffic/channel', { params }),

  getBounceSummary: (params: BounceParams) =>
    api.get<ApiEnvelope<BounceSummary>>('/site-data/bounce/summary', { params }),
  getBounceTrend: (params: BounceParams) =>
    api.get<ApiEnvelope<{ list: BounceTrendRow[] }>>('/site-data/bounce/trend', { params }),
  getBounceChannel: (params: BounceParams) =>
    api.get<ApiEnvelope<{ list: BounceChannelRow[]; level: number }>>('/site-data/bounce/channel', { params }),
  getBounceDetail: (params: BounceParams) =>
    api.get<ApiEnvelope<{ list: BounceDetailRow[]; group_by: string }>>('/site-data/bounce/detail', { params }),

  getAcqTrend: (params: AcqParams) =>
    api.get<ApiEnvelope<{ list: AcqTrendRow[] }>>('/site-data/channel-acq/trend', { params }),
  getAcqOptions: () =>
    api.get<ApiEnvelope<AcqChannelOptions>>('/site-data/channel-acq/options'),
  getAcqQuality: (params: AcqParams) =>
    api.get<ApiEnvelope<{ list: AcqQualityRow[]; level: number }>>('/site-data/channel-acq/quality', { params }),
  getAcqDetail: (params: AcqParams) =>
    api.get<ApiEnvelope<AcqPagedList<AcqDetailRow>>>('/site-data/channel-acq/detail', { params }),
  getAcqCost: (params: AcqParams & { kind?: 'cost' | 'layered' | 'detail' }) =>
    api.get<ApiEnvelope<AcqPagedList<AcqCostRow> & { kind: string }>>('/site-data/channel-acq/cost', { params }),

  getUserPortalCore: (params: UserPortalCoreParams) =>
    api.get<ApiEnvelope<UserPortalCoreData>>('/site-data/user-portal/core', { params }),
  getUserPortalTrend: (params: UserPortalRangeParams) =>
    api.get<ApiEnvelope<{ list: UserPortalTrendRow[] }>>('/site-data/user-portal/trend', { params }),
  getUserPortalTransaction: (params: UserPortalTableParams) =>
    api.get<ApiEnvelope<{ list: UserPortalTransactionRow[] }>>('/site-data/user-portal/transaction', { params }),
  getUserPortalSession: (params: UserPortalTableParams) =>
    api.get<ApiEnvelope<{ list: UserPortalSessionRow[] }>>('/site-data/user-portal/session', { params }),
  getUserPortalAcquisition: (params: UserPortalTableParams) =>
    api.get<ApiEnvelope<{ list: UserPortalAcquisitionRow[] }>>('/site-data/user-portal/acquisition', { params }),
  getUserPortalInstall: (params: UserPortalTableParams) =>
    api.get<ApiEnvelope<{ list: UserPortalInstallRow[] }>>('/site-data/user-portal/install', { params }),
  getUserPortalNewRepurchase: (params: UserPortalRepurchaseParams) =>
    api.get<ApiEnvelope<{ list: UserPortalNewRepurchaseRow[] }>>('/site-data/user-portal/new-repurchase', { params }),
  getUserPortalOldRepurchase: (params: UserPortalRepurchaseParams) =>
    api.get<ApiEnvelope<{ list: UserPortalOldRepurchaseRow[] }>>('/site-data/user-portal/old-repurchase', { params }),

  getPortalOptions: (params: { field: PortalOptionField; site?: string }) =>
    api.get<ApiEnvelope<PortalOptions>>('/site-data/channel-portal/options', { params }),
  getPortalCore: (params: PortalParams) =>
    api.get<ApiEnvelope<{ list: PortalCoreCard[]; date: string }>>('/site-data/channel-portal/core', { params }),
  getPortalTrend: (params: PortalParams) =>
    api.get<ApiEnvelope<{ list: PortalTrendRow[] }>>('/site-data/channel-portal/trend', { params }),
  getPortalSession: (params: PortalParams) =>
    api.get<ApiEnvelope<{ list: PortalSessionRow[] }>>('/site-data/channel-portal/session', { params }),
  getVooglamPortalSessionMultidim: (params: PortalParams) =>
    api.get<ApiEnvelope<{ list: VooglamPortalSessionMultidimRow[] }>>('/site-data/channel-portal/vooglam-session-multidim', { params }),
  getVooglamPortalSessionComparison: (params: PortalParams) =>
    api.get<ApiEnvelope<{ list: VooglamSessionComparisonRow[] }>>('/site-data/channel-portal/vooglam-session-comparison', { params }),
  getPortalUser: (params: PortalParams) =>
    api.get<ApiEnvelope<{ list: PortalUserRow[] }>>('/site-data/channel-portal/user', { params }),
  getPortalPayUser: (params: PortalParams) =>
    api.get<ApiEnvelope<{ list: PortalPayUserRow[]; period: string }>>('/site-data/channel-portal/pay-user', { params }),
  getPortalProduct: (params: PortalParams) =>
    api.get<ApiEnvelope<{ list: PortalProductRow[] }>>('/site-data/channel-portal/product', { params }),

  // ── 花费类型监控 ──────────────────────────────────────────────
  getCostTypeTable: (params: CostTypeParams) =>
    api.get<ApiEnvelope<{ list: CostVPivotRow[] }>>('/site-data/cost-type/table', { params }),
  getCostTypeByType1: (params: CostTypeParams) =>
    api.get<ApiEnvelope<{ list: CostPieRow[] }>>('/site-data/cost-type/by-type1', { params }),
  getCostTypeType2Breakdown: (params: CostTypeParams) =>
    api.get<ApiEnvelope<{ data: CostType2Breakdown }>>('/site-data/cost-type/type2-breakdown', { params }),
  getCostTypeEffectAd: (params: CostTypeParams) =>
    api.get<ApiEnvelope<{ list: CostPieRow[] }>>('/site-data/cost-type/effect-ad', { params }),
  getCostTypePaidChannel: (params: CostTypeParams) =>
    api.get<ApiEnvelope<{ list: CostPieRow[] }>>('/site-data/cost-type/paid-channel', { params }),
  getCostTypeVMarketingShare: (params: CostTypeParams) =>
    api.get<ApiEnvelope<{ list: CostVPivotRow[] }>>('/site-data/cost-type/v-marketing-share', { params }),
  getCostTypeVAdBreakdown: (params: CostTypeParams) =>
    api.get<ApiEnvelope<{ list: CostVPivotRow[] }>>('/site-data/cost-type/v-ad-breakdown', { params }),
  getCostTypeFilters: (params: { site: string }) =>
    api.get<ApiEnvelope<{ data: CostTypeFilters }>>('/site-data/cost-type/filters', { params }),
  getCostTypeVFilters: (params: { site: string; start_date?: string; end_date?: string }) =>
    api.get<ApiEnvelope<{ data: CostTypeFilters }>>('/site-data/cost-type/v-filters', { params }),

  // ── 花费目标分层看板 ──────────────────────────────────────────
  getTargetTypeTable: (params: TargetTypeParams) =>
    api.get<ApiEnvelope<{ list: TargetTypeRow[] }>>('/site-data/target-type/table', { params }),
  getTargetTypePie: (params: TargetTypeParams & { kind: 'effect' | 'brand' | 'new' | 'old' }) =>
    api.get<ApiEnvelope<{ list: TargetTypePieRow[] }>>('/site-data/target-type/pie', { params }),

  // ── 投放目标跟进看板 ──────────────────────────────────────────
  getDeliveryTargetChannel: (params: DeliveryTargetParams) =>
    api.get<ApiEnvelope<{ list: DeliveryTargetChannelRow[]; period: string }>>('/site-data/delivery-target/channel', { params }),
  getDeliveryTargetPerson: (params: DeliveryTargetParams) =>
    api.get<ApiEnvelope<{ list: DeliveryTargetPersonRow[]; period: string }>>('/site-data/delivery-target/person', { params }),
};

// ── 花费目标分层看板 (Z站运营中心 - 营销分析) ───────────────────────
export type TargetTypePeriod = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'half_year';

export interface TargetTypeRow {
  period?: string;
  dimensions: Record<string, string>;
  spend: number;
  net_sales: number;
  cost_rate: number;
}

export interface TargetTypePieRow {
  platform: string;
  cost_rate: number;
}

export interface TargetTypeParams {
  site: string;
  start_date: string;
  end_date: string;
  period?: TargetTypePeriod;
  platform?: string;
  cost_type?: string;
  new_old_type?: string;
  dimensions?: string;
}

// ── 渠道门户 (Z站运营中心 - 营销分析) ───────────────────────────────
export type PortalPeriod = 'day' | 'week' | 'month' | 'year';

export interface PortalCoreCard {
  key: string;
  label: string;
  value: number;
  is_pct: boolean;
  dod: number; // 日环比
  wow: number; // 周同比
}

export interface PortalTrendRow {
  date: string;
  session_num: number;
  net_sales: number;
  order_num: number;
  first_user_num: number;
  reg_first_rate: number;
  spend: number;
  session_cost: number;
  cvr: number;
  cpa: number;
  roi: number;
}

export interface PortalSessionRow {
  date: string;
  one_level_channel: string;
  two_level_channel: string;
  three_level_channel: string;
  country: string;
  source: string;
  medium: string;
  campaign: string;
  device: string;
  channel: string;
  session_num: number;
  regist_user_num: number;
  pdp_view_num: number;
  add_cart_num: number;
  order_num: number;
  net_sales: number;
  avg_price: number;
  session_reg_rate: number;
  cvr: number;
  session_cart_rate: number;
  cart_conv_rate: number;
}

export interface PortalUserRow {
  date: string;
  one_level_channel: string;
  two_level_channel: string;
  three_level_channel: string;
  country: string;
  device: string;
  channel: string;
  visit_user_num: number;
  no_reg_visit_num: number;
  no_reg_visit_rate: number;
  first_visit_num: number;
  first_visit_rate: number;
  reg_num: number;
  reg_rate: number;
}

export interface PortalPayUserRow {
  period: string;
  one_level_channel: string;
  two_level_channel: string;
  three_level_channel: string;
  country: string;
  device: string;
  channel: string;
  first_order_reg_cur: number;
  first_order_reg_year: number;
  first_order_no_year: number;
  new_repeat_num: number;
  act_old_num: number;
  sleep_old_num: number;
}

export interface PortalProductRow {
  date: string;
  one_level_channel: string;
  two_level_channel: string;
  three_level_channel: string;
  channel: string;
  product_sku: string;
  business_sku: string;
  shop_sku: string;
  pdp_view_num: number;
  add_cart_num: number;
  quantity: number;
  sales_amt: number;
  avg_price: number;
  view_conv_rate: number;
}

/** Vooglam 渠道门户专属：会话多维报表。 */
export interface VooglamPortalSessionMultidimRow {
  date: string;
  two_level_channel: string;
  three_level_channel: string;
  spend: number;
  clicks: number;
  session_num: number;
  arrival_rate: number;
  session_cost: number;
  regist_user_num: number;
  pdp_view_num: number;
  add_cart_num: number;
  order_num: number;
  net_sales: number;
  roi: number;
  cpa: number;
  avg_price: number;
  session_reg_rate: number;
  cvr: number;
  session_cart_rate: number;
  cart_conv_rate: number;
}

/** Vooglam 渠道门户专属：FB 剔除口径下的同期对比汇总。 */
export interface VooglamSessionComparisonRow {
  label: string;
  is_growth: boolean;
  spend: number;
  session_num: number;
  session_cost: number;
  pdp_view_num: number;
  add_cart_num: number;
  order_num: number;
  net_sales: number;
  roi: number;
  cpa: number;
  avg_price: number;
  session_reg_rate: number;
  cvr: number;
  session_cart_rate: number;
  cart_conv_rate: number;
}

export interface PortalParams {
  site: string;
  start_date: string;
  end_date: string;
  period?: PortalPeriod;
  channels?: string;     // 逗号分隔的一级渠道
  two_level?: string;
  three_level?: string;
  country?: string;
  device?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  full_name?: string;
  product_sku?: string;
  business_sku?: string;
  shop_sku?: string;
  dimensions?: string;
  /** 逗号分隔的下拉筛选字段名；对应字段值按排除条件处理。 */
  exclude?: string;
}

export type PortalOptionField = 'device' | 'one_level' | 'two_level' | 'three_level' | 'country' | 'source' | 'medium' | 'product_category';

export interface PortalOptions {
  list: string[];
}

// ── 渠道拉新效果监控 (Z站运营中心 - 用户分析) ───────────────────────
export type AcqPeriod = 'day' | 'month' | 'quarter' | 'year';

export interface AcqTrendRow {
  channel: string;
  new_user_num: number;
  paid_user_num: number;
  new_user_rate: number;
}

export interface AcqQualityRow {
  one_level_channel: string;
  two_level_channel?: string;
  new_user_num: number;
  new_avg_price: number;   // 新客单均价
  new_cust_price: number;  // 新客客单价
  new_first_avg: number;   // 新客首单单均价
}

export interface AcqDetailRow {
  dimensions?: Record<string, string>;
  channel: string;
  date: string;
  paid_user_num: number;
  order_num: number;
  new_base_payable: number;
  new_order_num: number;
  new_user_num: number;
  new_user_rate: number;
  new_cust_price: number;
  new_avg_price: number;
  first_order_pay: number;
  new_first_avg: number;
}

export interface AcqCostRow {
  dim: string;
  date: string;
  spend: number;
  uv: number;
  uv_cost: number;
  order_num: number;
  cvr: number;
  paid_user_num: number;
  new_base_payable: number;
  new_order_num: number;
  new_user_num: number;
  new_user_spend: number;
  new_acq_cost: number;
  new_user_rate: number;
  new_cust_price: number;
  new_avg_price: number;
  first_order_pay: number;
  new_first_avg: number;
}

export interface AcqChannelOptions {
  one_level: string[];
  two_level: string[];
  three_level: string[];
  source: string[];
  medium: string[];
  campaign: string[];
}

export interface AcqPagedList<T> {
  list: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface AcqParams {
  site: string;
  start_date: string;
  end_date: string;
  period?: AcqPeriod;
  level?: 1 | 2;
  one_level_channel?: string;
  two_level_channel?: string;
  three_level_channel?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  dimensions?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  /** 逗号分隔的下拉筛选字段名；对应字段值按排除条件处理。 */
  exclude?: string;
}

// ── 用户门户 (Z站运营中心 - 用户分析) ───────────────────────────────
export type UserPortalPeriod = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type UserPortalAcqPeriod = 'day' | 'week' | 'month';

export interface UserPortalCoreParams {
  site: string;
  date: string;
}

export interface UserPortalRangeParams {
  site: string;
  start_date: string;
  end_date: string;
}

export interface UserPortalTableParams extends UserPortalRangeParams {
  period?: UserPortalPeriod | UserPortalAcqPeriod;
  store_name?: string;
  device?: string;
  new_old_user?: string;
  user_tag?: string;
  one_level_channel?: string;
  two_level_channel?: string;
  three_level_channel?: string;
  traffic_type?: string;
  dimensions?: string;
}

export interface UserPortalRepurchaseParams {
  site: string;
  year: number;
}

export interface UserPortalCoreCard {
  key: string;
  label: string;
  value: number;
  is_pct: boolean;
  dod: number;
  wow: number;
  seven: number;
  mtd: number;
}

export interface UserPortalProgressRow {
  key: string;
  label: string;
  progress: number;
  has_target: boolean;
}

export interface UserPortalCoreData {
  cards: UserPortalCoreCard[];
  progress: UserPortalProgressRow[];
}

export interface UserPortalTrendRow {
  date: string;
  net_sales: number;
  net_sales_last_year: number;
  new_net_sales: number;
  new_net_sales_last_year: number;
  old_net_sales: number;
  old_net_sales_last_year: number;
  register_users: number;
  register_users_last_year: number;
}

export interface UserPortalTransactionRow {
  period: string;
  one_level_channel?: string;
  new_old_user: string;
  user_tag: string;
  net_sales: number;
  payable_user_num: number;
  customer_price: number;
  payable_order_num: number;
  order_avg_price: number;
  product_quantity: number;
  product_avg_price: number;
  frequency: number;
  joint_rate: number;
  visitor_user_num: number;
  login_purchase_rate: number;
}

export interface UserPortalSessionRow {
  period: string;
  one_level_channel?: string;
  session_num: number;
  order_num: number;
  session_cvr: number;
  visit_rate?: number;
  detail_view_rate?: number;
  add_cart_rate?: number;
  cart_view_rate?: number;
  cart_checkout_rate?: number;
  order_create_rate?: number;
  pay_success_rate?: number;
  frequency?: number;
  product_detail_view_users?: number;
  add_cart_users?: number;
  cart_view_users?: number;
  pay_page_users?: number;
  create_order_users?: number;
  pay_user_num?: number;
}

export interface UserPortalAcquisitionRow {
  period: string;
  first_visit_user_num: number;
  register_user_num: number;
  register_rate: number;
  first_day_register_rate: number;
  first_order_user_num: number;
  register_day_first_order_rate: number;
  no_register_visit_user_num: number;
  register_no_order_user_num: number;
  year_no_month_goal_user_num?: number;
  year_no_month_visit_user_num?: number;
  year_no_month_first_order_num?: number;
  year_no_month_visit_rate?: number;
  year_no_month_purchase_rate?: number;
  his_year_goal_user_num?: number;
  his_year_visit_user_num?: number;
  his_year_first_order_num?: number;
  his_year_visit_rate?: number;
  his_year_purchase_rate?: number;
}

export interface UserPortalInstallRow {
  period: string;
  install_user_num: number;
}

export interface UserPortalRepurchaseBucket {
  label: string;
  user_count: number;
  order_count: number;
  net_sales: number;
  rate: number;
  visit_user_count?: number;
  visit_rate?: number;
}

export interface UserPortalNewRepurchaseRow {
  period: string;
  base_user_count: number;
  base_order_count: number;
  base_net_sales: number;
  base_second_purchase_user_count?: number;
  base_second_purchase_rate?: number;
  buckets: UserPortalRepurchaseBucket[];
  total: UserPortalRepurchaseBucket;
  is_total: boolean;
}

export interface UserPortalOldRepurchaseRow {
  first_year: string;
  base_user_count: number;
  base_order_count: number;
  base_net_sales: number;
  buckets: UserPortalRepurchaseBucket[];
  year_total: UserPortalRepurchaseBucket;
  is_total: boolean;
}

// ── 花费类型监控 (Z站运营中心 - 营销分析) ────────────────────────────
export type CostTypePeriod = 'day' | 'week' | 'month' | 'quarter' | 'year';

// 花费监控看板表格一行（图1，按日期维度聚合）。
export interface CostTypeTableRow {
  date: string;       // 日期维度标签（或「总计」）
  spend: number;      // 费用
  net_sales: number;  // 净销售额
  ratio: number;      // 费用占比
  is_total: boolean;  // 是否总计行
}

export interface CostVPivotRow {
  dimensions: Record<string, string>;
  spend: number;
  net_sales: number;
  ratio: number;
  grouping_id: number;
  id: string;
  parent_id: string;
  level: number;
  has_children: boolean;
  is_total: boolean;
}

// 占比饼图通用一行（图2/图4/图5）。
export interface CostPieRow {
  dim: string;    // 维度名
  ratio: number;  // 费用占比 = 该维度 spend / 全量净销售额
  spend: number;  // 该维度费用
}

// 整站营销占比-明细表（图3）的固定 6 指标。
export interface CostType2Breakdown {
  effect_ad: number; // 效果广告营销费用
  user_mkt: number;  // 用户营销
  kol: number;       // 红人营销
  alliance: number;  // 联盟
  other: number;     // 其他
  brand_mkt: number; // 品牌营销费用
}

export interface CostTypeFilters {
  cost_type_1: string[]; // 营销费用大类
  cost_type_2: string[]; // 营销费用一级分类
  cost_type_3: string[]; // 营销费用二级分类
  platform: string[];    // 效果营销平台 cost_breakdown_1
  new_old_type: string[]; // 广告新老客 new_old_type
  target_type: string[];  // 流量类型 target_type
  breakdown: string[];   // 费用细分 cost_breakdown_2
}

export interface CostTypeParams {
  site: string;
  start_date: string;
  end_date: string;
  period?: CostTypePeriod;
  cost_type_1?: string;
  cost_type_2?: string;
  cost_type_3?: string;
  platform?: string;
  new_old_type?: string;
  target_type?: string;
  breakdown?: string;
  dimensions?: string;
}

// ── 投放目标跟进看板 (Z站运营中心 - 营销分析) ───────────────────────
export type DeliveryTargetPeriod = 'month' | 'quarter' | 'year';

// 渠道完成进度跟进一行（图1，27 指标）。
export interface DeliveryTargetChannelRow {
  channel: string;
  sales_target: number;  // 销售额目标（物理表无→0）
  revenue: number;       // 收入
  progress: number;      // 完成进度（目标0→0）
  spend: number;         // 花费
  order_num: number;     // 订单量
  cpa: number;
  roi: number;
  avg_price: number;     // 单均价
  buy_rate: number;      // 购买率
  clicks: number;        // 点击量
  cpc: number;
  new_spend: number;     // 账户新客花费
  session_num: number;   // 神策会话数
  sce_order_num: number; // 神策订单量
  session_cost: number;  // 神策会话成本
  arrival_rate: number;  // 到达率
  sce_conv_rate: number; // 神策转化率
  sce_cpa: number;       // 神策CPA
  sce_roi: number;       // 神策ROI
  sce_sales: number;     // 神策销售额
  user_ct: number;       // 获客数
  new_sales: number;     // 新客销售额
  new_order_num: number; // 新客订单量
  new_user_num: number;  // 新客数
  new_user_rate: number; // 新客占比
  new_avg_price: number; // 新客单均价
  new_acq_cost: number;  // 新客获客成本
}

// 人员完成进度跟进一行（图2，11 指标）。
export interface DeliveryTargetPersonRow {
  platform: string;     // 平台
  person: string;       // 人员
  sales_target: number; // 销售额目标（物理表无→0）
  revenue: number;      // 收入
  progress: number;     // 完成进度（目标0→0）
  spend: number;        // 花费
  order_num: number;    // 订单量
  cpa: number;
  roi: number;
  avg_price: number;    // 单均价
  buy_rate: number;     // 购买率
  clicks: number;       // 点击量
  cpc: number;
}

export interface DeliveryTargetParams {
  period?: DeliveryTargetPeriod; // month(默认)/quarter/year
  start_date: string;            // 起始周期首日 YYYY-MM-DD
  end_date: string;              // 结束周期首日 YYYY-MM-DD
}
